import { AxiosError } from 'axios';
import { useMemo, useState } from 'react';
import ReactGA from 'react-ga4';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ITractorImage } from '../../../../api/user/mutations/postUsersSellTractors';
import { SellTractorImageUploadButton } from '../../../../components/common/buttons/SellTractorImageUploadButton';
import { PopupConfirmModal } from '../../../../components/common/modal/popup/PopupConfirmModal';
import { SlideConfirmModal } from '../../../../components/common/modal/slide/SlideConfirmModal';
import { SurveyForm } from '../../../../components/common/survey/form/SurveyForm';
import { SurveyInputHeader } from '../../../../components/common/survey/inputs/SurveyInputHeader';
import { pageInfo } from '../../../../constants/pageInfo/pageInfo.constant';
import { OpenMarketProductStatusConstant, ProductStatusConstant } from '../../../../constants/product/product.constant';
import { sellTractorSurveySteps } from '../../../../constants/survey/sellTractorSurvey';
import { usePatchUserSellTractorsMutation } from '../../../../hooks/api/user/mutations/usePatchUserSellTractors.mutation';
import { usePostUsersSellTractorsMutation } from '../../../../hooks/api/user/mutations/usePostUsersSellTractors.mutation';
import { usePopup } from '../../../../hooks/popup/usePopup';
import { useAppDispatch, useAppSelector } from '../../../../hooks/store/useStore';
import { useNotification } from '../../../../provider/ToastNotificationProvider';
import { imageTypes, tractorImageType, userSellTractorSurveySlice } from '../../../../store/userSellTractorSurveySlice';
import { SellTractorSurveyPageWrapper } from '../SellTractorSurveyPageWrapper';
import { SellTractorImageTooltip } from './components/SellTractorImageTooltip';

export const SellTractorSurveyTractorRequiredImagePage = () => {
  const navigate = useNavigate();
  const Popup = usePopup();

  const [isSlideModalOpen, setIsSlideModalOpen] = useState<boolean>(false);
  const [isPopupModalOpen, setIsPopupModalOpen] = useState<boolean>(false);
  const [isRerequestPopupModalOpen, setIsRerequestPopupModalOpen] = useState<boolean>(false);
  const [isSyetemErrorPopupModalOpen, setIsSyetemErrorPopupModalOpen] = useState<boolean>(false);
  const [isImageUploadFailedModalOpen, setIsImageUploadFailedModalOpen] = useState<boolean>(false);

  const dispatch = useAppDispatch();
  const userSellTractorSurveyActions = userSellTractorSurveySlice.actions;
  const userSellTractorSurveySelector = useAppSelector((state) => state.userSellTractorSurvey);
  const { mutate: postUsersSellTractor, isLoading: isPostUsersSellTractorLoading } = usePostUsersSellTractorsMutation();
  const { mutate: patchUserSellTractor, isLoading: isPatchUserSellTractorLoading } = usePatchUserSellTractorsMutation();

  const currentStep = sellTractorSurveySteps.STEP8;
  const steps = Object.keys(sellTractorSurveySteps);
  const { openNotification } = useNotification();

  const tractorImages = useMemo<ITractorImage[]>(() => {
    const tractorImages: ITractorImage[] = [];

    imageTypes.forEach((type) => {
      if (userSellTractorSurveySelector.tractorImageUrls[type]) {
        tractorImages.push({ type, url: userSellTractorSurveySelector.tractorImageUrls[type] });
      }
    });

    return tractorImages;
  }, [userSellTractorSurveySelector.tractorImageUrls]);

  const isAllImageUploaded = useMemo<boolean>(() => {
    if (
      userSellTractorSurveySelector.tractorImageUrls.LEFT &&
      userSellTractorSurveySelector.tractorImageUrls.FRONT &&
      userSellTractorSurveySelector.tractorImageUrls.BACK &&
      userSellTractorSurveySelector.tractorImageUrls.INNER
    ) {
      return true;
    }

    return false;
  }, [userSellTractorSurveySelector.tractorImageUrls]);

  const handleDeleteImage = (imageType: tractorImageType) => {
    dispatch(
      userSellTractorSurveyActions.changeTractorImageUrls({
        property: imageType,
        imageUrl: '',
      }),
    );
  };

  const handleUploadImage = (imageType: tractorImageType, imageUrl: string) => {
    dispatch(
      userSellTractorSurveyActions.changeTractorImageUrls({
        property: imageType,
        imageUrl,
      }),
    );
  };

  const confirmSlideModal = () => {
    setIsSlideModalOpen(false);
    navigate('/survey/entry/sell-tractor', { replace: true });
  };

  const closeSlideModal = () => {
    setIsSlideModalOpen(false);
    setIsPopupModalOpen(true);
  };

  const confirmPopupModal = () => {
    setIsPopupModalOpen(false);
    navigate(pageInfo.openMarketListPage.path, { replace: true });
  };

  const updateUserOwnTractor = () => {
    patchUserSellTractor(
      {
        sellTractorId: userSellTractorSurveySelector.sellTractorIdForEdit!,
        reqBody: {
          manufacturedYear: Number(userSellTractorSurveySelector.manufacturedYear),
          operationTime: Number(userSellTractorSurveySelector.operationTime),
          option: userSellTractorSurveySelector.option ?? null,
          optionDetails: userSellTractorSurveySelector.optionDetails ?? null,
          workingMachine: userSellTractorSurveySelector.workingMachine ?? null,
          workingMachineDetails: userSellTractorSurveySelector.workingMachineDetails ?? null,
          hopeSellingPrice: userSellTractorSurveySelector.hopeSellingPrice! * 10000,
          tractorImages,
          location: userSellTractorSurveySelector.location!,
          directImportYn: userSellTractorSurveySelector.directImportYn ?? null,
          priceNegotiationYn: userSellTractorSurveySelector.priceNegotiationYn ?? null,
          frontTireCondition: userSellTractorSurveySelector.frontTireCondition ?? null,
          rearTireCondition: userSellTractorSurveySelector.rearTireCondition ?? null,
          needRepairParts: userSellTractorSurveySelector.needRepairParts ?? null,
          replacedParts: userSellTractorSurveySelector.replacedParts ?? null,
          information: userSellTractorSurveySelector.information!,
          approvalStatus: OpenMarketProductStatusConstant.ONGOING,
          status: ProductStatusConstant.ONSALE,
        },
      },
      {
        onSuccess: (data) => {
          if (isRerequestPopupModalOpen) {
            setIsRerequestPopupModalOpen(false);
          }

          openNotification({ message: '정보 수정 및 심사 신청이 완료 되었습니다.' });
          navigate(pageInfo.myInfoSellTractorListPage.path, { replace: true });
        },
        onError: (error) => {
          if (error instanceof AxiosError) {
            if (!error.response) {
              setIsRerequestPopupModalOpen(true);
            } else {
              setIsSyetemErrorPopupModalOpen(true);
            }
          }
        },
      },
    );
  };

  const handleSurveySubmit = () => {
    if (userSellTractorSurveySelector.sellTractorIdForEdit) {
      updateUserOwnTractor();
      return;
    }
    postUsersSellTractor(
      {
        reqBody: {
          userOwnTractorId: userSellTractorSurveySelector.userOwnTractorId ?? null,
          brandId: userSellTractorSurveySelector.brandId!,
          model: userSellTractorSurveySelector.model!,
          engineOutputInPs: Number(userSellTractorSurveySelector.engineOutputInPs),
          manufacturedYear: Number(userSellTractorSurveySelector.manufacturedYear),
          operationTime: Number(userSellTractorSurveySelector.operationTime),
          option: userSellTractorSurveySelector.option ?? null,
          optionDetails: userSellTractorSurveySelector.optionDetails ?? null,
          workingMachine: userSellTractorSurveySelector.workingMachine ?? null,
          workingMachineDetails: userSellTractorSurveySelector.workingMachineDetails ?? null,
          hopeSellingPrice: userSellTractorSurveySelector.hopeSellingPrice! * 10000,
          tractorImages,
          location: userSellTractorSurveySelector.location!,
          directImportYn: userSellTractorSurveySelector.directImportYn ?? null,
          priceNegotiationYn: userSellTractorSurveySelector.priceNegotiationYn ?? null,
          frontTireCondition: userSellTractorSurveySelector.frontTireCondition ?? null,
          rearTireCondition: userSellTractorSurveySelector.rearTireCondition ?? null,
          needRepairParts: userSellTractorSurveySelector.needRepairParts ?? null,
          replacedParts: userSellTractorSurveySelector.replacedParts ?? null,
          information: userSellTractorSurveySelector.information!,
        },
      },
      {
        onSuccess: (data) => {
          if (isRerequestPopupModalOpen) {
            setIsRerequestPopupModalOpen(false);
          }

          setIsSlideModalOpen(true);
          ReactGA.event('pvm_sell_tractor_complete', { category: 'pvm', action: 'pvm_sell_tractor_complete' });
        },
        onError: (error) => {
          if (error instanceof AxiosError) {
            if (!error.response) {
              setIsRerequestPopupModalOpen(true);
            } else {
              setIsSyetemErrorPopupModalOpen(true);
            }
          }
        },
      },
    );
  };

  const handleToolTipClick = () => navigate('/survey/tip/sell-tractor', { replace: true });

  const handleImageUploadError = () => setIsImageUploadFailedModalOpen(true);

  return (
    <>
      <SellTractorSurveyPageWrapper
        headerTitle={'매물 사진을 등록해주세요'}
        isLoading={isPostUsersSellTractorLoading}
        canConfirm={isAllImageUploaded}
        confirmButtonText={'완료'}
        confirmHandler={handleSurveySubmit}
        prevButtonHandler={() => navigate('/survey/progress/sell-tractor/extra-info-2', { replace: true })}
        buttonTooltip={<SellTractorImageTooltip onButtonClick={handleToolTipClick} />}
        steps={steps}
        currentStep={currentStep}
      >
        <SurveyForm id={'survey_page_wrapper'}>
          <ContentsContainer>
            <SurveyInputHeader inputTitle={'매물 필수 사진 (4장 필수)'} isRequired />

            <ImageUploadSection>
              <SellTractorImageUploadButton
                inputId="tractor-image-left"
                buttonText={'좌측면 (대표사진)'}
                uploadedImageUrl={userSellTractorSurveySelector.tractorImageUrls.LEFT}
                successHandler={(imageUrl: string) => handleUploadImage('LEFT', imageUrl)}
                errorHandler={handleImageUploadError}
                deleteHandler={() => handleDeleteImage('LEFT')}
              />

              <SellTractorImageUploadButton
                inputId="tractor-image-front"
                buttonText={'정면'}
                uploadedImageUrl={userSellTractorSurveySelector.tractorImageUrls.FRONT}
                successHandler={(imageUrl: string) => handleUploadImage('FRONT', imageUrl)}
                errorHandler={handleImageUploadError}
                deleteHandler={() => handleDeleteImage('FRONT')}
              />

              <SellTractorImageUploadButton
                inputId="tractor-image-back"
                buttonText={'후면'}
                uploadedImageUrl={userSellTractorSurveySelector.tractorImageUrls.BACK}
                successHandler={(imageUrl: string) => handleUploadImage('BACK', imageUrl)}
                errorHandler={handleImageUploadError}
                deleteHandler={() => handleDeleteImage('BACK')}
              />

              <SellTractorImageUploadButton
                inputId="tractor-image-inner1"
                buttonText={'실내'}
                uploadedImageUrl={userSellTractorSurveySelector.tractorImageUrls.INNER}
                successHandler={(imageUrl: string) => handleUploadImage('INNER', imageUrl)}
                errorHandler={handleImageUploadError}
                deleteHandler={() => handleDeleteImage('INNER')}
              />
            </ImageUploadSection>
          </ContentsContainer>

          <ContentsContainer>
            <SurveyInputHeader inputTitle={'매물 추가 사진'} isOptional />
            <ImageUploadSection>
              <SellTractorImageUploadButton
                inputId="tractor-image-front-tier"
                buttonText={'앞 타이어'}
                uploadedImageUrl={userSellTractorSurveySelector.tractorImageUrls.FRONTTIER}
                successHandler={(fileUrl: string) => {
                  handleUploadImage('FRONTTIER', fileUrl);
                }}
                errorHandler={handleImageUploadError}
                deleteHandler={() => handleDeleteImage('FRONTTIER')}
              />

              <SellTractorImageUploadButton
                inputId="tractor-image-back-tier"
                buttonText={'뒷 타이어'}
                uploadedImageUrl={userSellTractorSurveySelector.tractorImageUrls.BACKTIER}
                successHandler={(fileUrl: string) => {
                  handleUploadImage('BACKTIER', fileUrl);
                }}
                errorHandler={handleImageUploadError}
                deleteHandler={() => handleDeleteImage('BACKTIER')}
              />

              <SellTractorImageUploadButton
                inputId="tractor-image-working-machine1"
                buttonText={'작업기'}
                uploadedImageUrl={userSellTractorSurveySelector.tractorImageUrls.WORKINGMACHINE1}
                successHandler={(fileUrl: string) => {
                  handleUploadImage('WORKINGMACHINE1', fileUrl);
                }}
                errorHandler={handleImageUploadError}
                deleteHandler={() => handleDeleteImage('WORKINGMACHINE1')}
              />

              <SellTractorImageUploadButton
                inputId="tractor-image-etc1"
                buttonText={'기타'}
                uploadedImageUrl={userSellTractorSurveySelector.tractorImageUrls.ETC1}
                successHandler={(fileUrl: string) => {
                  handleUploadImage('ETC1', fileUrl);
                }}
                errorHandler={handleImageUploadError}
                deleteHandler={() => handleDeleteImage('ETC1')}
              />

              <SellTractorImageUploadButton
                inputId="tractor-image-etc2"
                buttonText={'기타'}
                uploadedImageUrl={userSellTractorSurveySelector.tractorImageUrls.ETC2}
                successHandler={(fileUrl: string) => {
                  handleUploadImage('ETC2', fileUrl);
                }}
                errorHandler={handleImageUploadError}
                deleteHandler={() => handleDeleteImage('ETC2')}
              />

              <SellTractorImageUploadButton
                inputId="tractor-image-etc3"
                buttonText={'기타'}
                uploadedImageUrl={userSellTractorSurveySelector.tractorImageUrls.ETC3}
                successHandler={(fileUrl: string) => {
                  handleUploadImage('ETC3', fileUrl);
                }}
                errorHandler={handleImageUploadError}
                deleteHandler={() => handleDeleteImage('ETC3')}
              />
            </ImageUploadSection>
          </ContentsContainer>
        </SurveyForm>
      </SellTractorSurveyPageWrapper>

      <SlideConfirmModal
        isShow={isSlideModalOpen}
        height={210}
        description={'혹시 추가로 판매하실\n트랙터가 더 있으세요?'}
        confirmButton={{ handleClick: confirmSlideModal, text: '네, 더 있어요.' }}
        closeButton={{ handleClick: closeSlideModal, text: '없어요. 완료할게요.' }}
      />
      <PopupConfirmModal
        isShow={isPopupModalOpen}
        hasCloseButton={false}
        title={'등록 완료! 🎉'}
        description={'승인이 완료되면\n직거래 매물에 업로드 돼요!'}
        buttonArray={[
          {
            text: '직거래 매물 보러가기',
            onClick: confirmPopupModal,
            color: 'blue',
          },
        ]}
      />
      <PopupConfirmModal
        isShow={isRerequestPopupModalOpen}
        hasCloseButton={false}
        closeModal={() => setIsRerequestPopupModalOpen(false)}
        description={'요청 시간이 초과되었어요 🙂\n아래 버튼을 눌러 다시 요청해주세요!'}
        buttonArray={[
          {
            text: '등록 완료 하기',
            onClick: handleSurveySubmit,
            color: 'blue',
            isLoading: isPostUsersSellTractorLoading,
            disabled: isPostUsersSellTractorLoading,
          },
        ]}
      />
      <PopupConfirmModal
        isShow={isSyetemErrorPopupModalOpen}
        hasCloseButton={false}
        closeModal={() => setIsRerequestPopupModalOpen(false)}
        description={'시스템 에러로 등록이 불가능합니다👨🏻‍🔧\n잠시후에 다시 이용해주세요.'}
        buttonArray={[
          {
            text: '홈으로',
            onClick: () => navigate(pageInfo.homePage.path, { replace: true }),
            color: 'blue',
          },
        ]}
      />
      <PopupConfirmModal
        isShow={isImageUploadFailedModalOpen}
        hasCloseButton={false}
        title={'통신이 원활하지 못해요 👨🏻‍🔧'}
        description={'통신 환경이 더 원활한 곳에서\n다시 시도 해주세요'}
        buttonArray={[
          {
            text: '홈으로',
            onClick: () => navigate(pageInfo.homePage.path, { replace: true }),
            color: 'blue',
          },
        ]}
      />
    </>
  );
};

const ContentsContainer = styled.div`
  display: flex;
  padding: 0px var(--radius-large, 16px) 24px var(--radius-large, 16px);
  flex-direction: column;
  align-items: flex-start;
  gap: var(--radius-large, 16px);
  align-self: stretch;
`;

const ImageUploadSection = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  gap: 8px;
`;

const ImageUploadTitle = styled.span`
  width: 100%;

  color: var(--Light-Scheme-Monochrome-offblack, #505363);

  /* System/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 85%; /* 13.6px */

  text-align: left;
`;

const TractorImageTipWrapper = styled.div`
  width: 100%;
  height: 350px;
  overflow-y: scroll;

  padding: 0px 26px;
`;

const TractorImage = styled.img`
  width: 100%;
`;
